/*!
 * DOOF - Dropdown Options Organizer Fieldforms - storefront renderer v0.1.0
 * Copyright (c) 2026 Macado Developments. All rights reserved. Proprietary; not licensed for redistribution.
 *
 * Renders product option fields from a JSON config and attaches them to the product form as
 * line item properties. The same file is used by the theme section (phase 1) and the
 * DOOF app block (phase 2) - both emit the same config shape (see README "Config format").
 */
/* global globalThis */
(function (global) {
  'use strict';

  var VERSION = '0.1.0';

  // ------------------------------------------------------------------ core (pure, DOM-free)

  var CHOICE_TYPES = ['dropdown', 'radio', 'buttons', 'color_swatch', 'image_swatch', 'checkbox_group'];
  var GROUP_TYPES = ['radio', 'buttons', 'color_swatch', 'image_swatch', 'checkbox_group'];
  var DISPLAY_TYPES = ['heading', 'paragraph', 'divider'];
  var INPUT_TYPES = ['text', 'textarea', 'number', 'date', 'color_picker', 'checkbox', 'file'];
  var KNOWN_TYPES = CHOICE_TYPES.concat(DISPLAY_TYPES, INPUT_TYPES);
  var OPERATORS = ['equals', 'not_equals', 'any_of', 'contains', 'not_contains', 'is_empty', 'is_not_empty', 'greater_than', 'less_than'];

  var DEFAULT_MESSAGES = {
    required: 'This field is required.',
    choose: 'Please make a selection.',
    minLength: 'Enter at least {n} characters.',
    maxLength: 'Enter no more than {n} characters.',
    pattern: 'Please match the requested format.',
    number: 'Enter a valid number.',
    min: 'Enter a value of {n} or more.',
    max: 'Enter a value of {n} or less.',
    minSelections: 'Select at least {n}.',
    maxSelections: 'Select no more than {n}.',
    fileSize: 'File must be smaller than {n} MB.',
    fileType: 'This file type is not allowed.',
    date: 'Enter a valid date.',
    dateMin: 'Choose a date on or after {n}.',
    dateMax: 'Choose a date on or before {n}.',
    chooseOption: 'Choose an option',
    chooseFile: 'Upload a file',
    choosePhoto: 'Upload your photo',
    noFile: 'No file chosen',
    removeFile: 'Remove',
    options: 'Selected options',
    formMissing: 'DOOF: the product form could not be found on this page. Set "Product form selector" in the section settings.',
    ruleMissing: 'DOOF: a condition on this field points to "{n}", but no field has that label.',
    dynamicCheckout: 'DOOF: the "Buy it now" button skips option fields. Turn off "Show dynamic checkout buttons" in this template\'s Buy buttons block.',
    extraCharges: 'Extra charges',
    buyNowCharges: 'Please use Add to cart: "Buy it now" cannot add the extra charge for your options.',
    addError: 'Could not add to cart: {n}. Please try again.',
    chargeMissing: 'Sorry, "{n}" can\'t be ordered online right now. Please contact us, or choose a different option.',
    chargeMissingEditor: 'DOOF: the extra charge "{n}" has no add-on product (not chosen, deleted, or not active on the Online Store). Customers choosing it are stopped at Add to cart until it has one.'
  };

  function has(list, v) { return list.indexOf(v) !== -1; }
  function norm(v) { return String(v == null ? '' : v).trim().toLowerCase(); }
  function str(v) { return v == null ? '' : String(v); }
  function num(v) {
    if (v === null || v === undefined || v === '') return null;
    var n = parseFloat(v);
    return isNaN(n) ? null : n;
  }
  function toCents(v) {
    var n = num(v);
    return n === null || n < 0 ? 0 : Math.round(n);
  }
  function fmt(msg, n) { return String(msg).replace('{n}', n); }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  /**
   * A date setting as YYYY-MM-DD. Accepts a fixed date, or a day offset from today:
   * "+1" (tomorrow), "-7", "today", "today+3".
   */
  function resolveDate(v, now) {
    var s = str(v).trim();
    if (!s) return '';
    var m = /^(today)?\s*([+-]?\s*\d+)?$/i.exec(s);
    if (!m || (!m[1] && !m[2])) return s;
    var d = now ? new Date(now) : new Date();
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() + (m[2] ? parseInt(m[2].replace(/\s/g, ''), 10) : 0));
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }

  function displayDate(iso) {
    try {
      return new Date(iso + 'T12:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) {
      return iso;
    }
  }

  function normalizeConditions(c) {
    if (!c || !c.rules || !c.rules.length) return null;
    var rules = c.rules
      .filter(function (r) { return r && str(r.field).trim() !== ''; })
      .map(function (r) {
        return { field: str(r.field).trim(), op: has(OPERATORS, r.op) ? r.op : 'equals', value: str(r.value) };
      });
    if (!rules.length) return null;
    return { action: c.action === 'hide' ? 'hide' : 'show', match: c.match === 'any' ? 'any' : 'all', rules: rules };
  }

  /** Flattens all option sets into one ordered field list with unique ids and property names. */
  function normalizeConfig(raw) {
    var cfg = raw || {};
    var fields = [];
    var seenIds = {};
    var seenNames = {};
    (cfg.sets || []).forEach(function (set) {
      (set && set.fields || []).forEach(function (f) {
        if (!f || !has(KNOWN_TYPES, f.type)) return;
        var id = str(f.id).trim() || 'f' + fields.length;
        while (seenIds[id]) id += '_';
        seenIds[id] = true;

        var seenOpt = {};
        var options = (f.options || [])
          .filter(function (o) { return o && str(o.label).trim() !== ''; })
          .map(function (o, i) {
            var oid = str(o.id).trim() || str(o.label).trim() || String(i);
            while (seenOpt[oid]) oid += '_';
            seenOpt[oid] = true;
            return {
              id: oid,
              label: str(o.label).trim(),
              price: toCents(o.price),
              color: str(o.color).trim(),
              image: str(o.image).trim(),
              isDefault: !!(o.default || o.isDefault)
            };
          });

        var label = str(f.label).trim();
        var name = '';
        if (!has(DISPLAY_TYPES, f.type)) {
          name = str(f.name).trim() || label || 'Option ' + (fields.length + 1);
          if (f.hideFromCart && name.charAt(0) !== '_') name = '_' + name;
          var base = name;
          var n = 2;
          while (seenNames[norm(name)]) name = base + ' (' + n++ + ')';
          seenNames[norm(name)] = true;
        }

        fields.push({
          id: id,
          setId: set.id,
          type: f.type,
          label: label,
          name: name,
          required: !!f.required && !has(DISPLAY_TYPES, f.type),
          help: str(f.help),
          placeholder: str(f.placeholder),
          content: str(f.content),
          width: f.width === 'half' ? 'half' : 'full',
          options: options,
          price: toCents(f.price),
          priceMode: has(['fixed', 'per_char', 'per_unit'], f.priceMode) ? f.priceMode : 'fixed',
          validation: f.validation || {},
          conditions: normalizeConditions(f.conditions),
          defaultValue: f.defaultValue,
          checkedValue: str(f.checkedValue).trim() || 'Yes',
          checkboxText: f.type === 'checkbox' ? str(f.checkboxText).trim() : '',
          custom: f.custom || null
        });
      });
    });
    // The product's own options (Size, Wood Type...) can be used in conditions like a field. They are
    // never rendered; their value is the current variant's, read from the product form.
    var product = cfg.product || {};
    var optionFields = (product.options || []).map(function (o, i) {
      var n = str(o && typeof o === 'object' ? o.name : o).trim();
      return n ? { id: '__option' + (i + 1), type: 'variant_option', index: i, label: n, name: n, options: [], validation: {}, conditions: null } : null;
    }).filter(Boolean);
    var variants = {};
    (product.variants || []).forEach(function (v) {
      if (v && v.id != null) variants[str(v.id)] = (v.options || []).map(str);
    });

    return {
      pricing: !!cfg.pricing,
      settings: cfg.settings || {},
      messages: Object.assign({}, DEFAULT_MESSAGES, cfg.messages || {}),
      product: product,
      fields: fields,
      optionFields: optionFields,
      ruleFields: fields.concat(optionFields),
      variants: variants,
      charges: normalizeCharges(cfg.charges)
    };
  }

  /** Values of the product option pseudo-fields for a variant id. */
  function variantValues(cfg, variantId) {
    var opts = cfg.variants[str(variantId)] || [];
    var out = {};
    cfg.optionFields.forEach(function (f) { out[f.id] = opts[f.index] || ''; });
    return out;
  }

  /**
   * Extra charges: add-on products added to the cart as their own line while their trigger holds
   * ("when field X is Y"). Theme-only pricing, the same method as YMQ and the stencil customizer.
   * Prices are presentment-currency cents straight from Liquid (variant.price).
   */
  function normalizeCharges(list) {
    return (list || [])
      .filter(function (c) { return c && (str(c.variantId).trim() !== '' || c.missing === true); })
      .map(function (c) {
        var value = str(c.value).trim();
        var variantId = str(c.variantId).trim();
        return {
          missing: !variantId, // its add-on product isn't available: refuse the option, never give it free
          variantId: variantId,
          price: toCents(c.price),
          title: str(c.title).trim() || 'Extra charge',
          field: str(c.field).trim(),
          op: has(OPERATORS, c.op) ? c.op : (value ? 'equals' : 'is_not_empty'),
          value: value,
          moveFields: (Array.isArray(c.moveFields) ? c.moveFields : str(c.moveFields).split(','))
            .map(function (s) { return str(s).trim(); }).filter(Boolean),
          qty: c.qty === 'one' ? 'one' : 'match'
        };
      });
  }

  /** Charges whose trigger holds. A charge with no field is always on; a hidden field never triggers. */
  function activeCharges(fields, values, visible, charges) {
    return (charges || []).filter(function (c) {
      if (!c.field) return true;
      var src = findField(fields, c.field);
      if (!src || !visible[src.id]) return false;
      return testRule(src, values[src.id], { op: c.op, value: c.value });
    });
  }

  /** Total of the charges a given choice would switch on, for the "(+$40.00)" next to it. */
  function chargePriceFor(field, charges, option) {
    return (charges || []).reduce(function (sum, c) {
      if (!c.field || findField([field], c.field) !== field) return sum;
      var hit;
      if (option) hit = c.op === 'equals' && (option.id === c.value || norm(option.label) === norm(c.value));
      else if (field.type === 'checkbox') hit = c.op === 'is_not_empty' || (c.op === 'equals' && testRule(field, true, c));
      else hit = c.op === 'is_not_empty';
      return hit ? sum + c.price : sum;
    }, 0);
  }

  function findField(fields, ref) {
    var r = norm(ref);
    for (var i = 0; i < fields.length; i++) if (fields[i].id === ref) return fields[i];
    for (var j = 0; j < fields.length; j++) {
      var f = fields[j];
      if (has(DISPLAY_TYPES, f.type)) continue;
      if (norm(f.label) === r || norm(f.name) === r || norm(f.name.replace(/^_/, '')) === r) return f;
    }
    return null;
  }

  function optionFor(field, id) {
    for (var i = 0; i < field.options.length; i++) if (field.options[i].id === id) return field.options[i];
    return null;
  }

  function selectedIds(field, v) {
    if (field.type === 'checkbox_group') return Array.isArray(v) ? v : [];
    return v ? [v] : [];
  }

  function isEmpty(field, v) {
    if (has(CHOICE_TYPES, field.type)) return selectedIds(field, v).length === 0;
    if (field.type === 'checkbox') return !v;
    if (field.type === 'file') return !v;
    return str(v).trim() === '';
  }

  function initialValue(field) {
    if (field.type === 'checkbox_group') {
      return field.options.filter(function (o) { return o.isDefault; }).map(function (o) { return o.id; });
    }
    if (has(CHOICE_TYPES, field.type)) {
      for (var i = 0; i < field.options.length; i++) if (field.options[i].isDefault) return field.options[i].id;
      return '';
    }
    if (field.type === 'checkbox') {
      var d = norm(field.defaultValue);
      return d === 'true' || d === 'yes' || d === 'checked' || field.defaultValue === true;
    }
    if (field.type === 'file') return null;
    return str(field.defaultValue);
  }

  function testRule(src, v, rule) {
    var op = rule.op;
    var target = rule.value;
    if (op === 'is_empty') return isEmpty(src, v);
    if (op === 'is_not_empty') return !isEmpty(src, v);
    if (op === 'any_of') {
      var list = str(target).split(',').map(norm).filter(Boolean);
      if (has(CHOICE_TYPES, src.type)) {
        return selectedIds(src, v).some(function (id) {
          var o = optionFor(src, id);
          return !!o && (has(list, norm(o.id)) || has(list, norm(o.label)));
        });
      }
      if (src.type === 'checkbox') return list.some(function (t) { return testRule(src, v, { op: 'equals', value: t }); });
      return has(list, norm(src.type === 'file' ? v && v.name : v));
    }

    var eq;
    var contains;
    if (has(CHOICE_TYPES, src.type)) {
      var hit = selectedIds(src, v).some(function (id) {
        var o = optionFor(src, id);
        return !!o && (o.id === target || norm(o.label) === norm(target));
      });
      eq = hit;
      contains = hit;
    } else if (src.type === 'checkbox') {
      var t = norm(target);
      var wantChecked = !(t === 'no' || t === 'false' || t === 'unchecked' || t === '0' || t === 'off');
      eq = !!v === wantChecked;
      contains = eq;
    } else if (src.type === 'number') {
      var a = num(v);
      var b = num(target);
      eq = a !== null && b !== null && a === b;
      contains = norm(target) !== '' && norm(v).indexOf(norm(target)) !== -1;
    } else if (src.type === 'file') {
      eq = !!v && norm(v.name) === norm(target);
      contains = !!v && norm(target) !== '' && norm(v.name).indexOf(norm(target)) !== -1;
    } else {
      eq = norm(v) === norm(target);
      contains = norm(target) !== '' && norm(v).indexOf(norm(target)) !== -1;
    }

    switch (op) {
      case 'equals': return eq;
      case 'not_equals': return !eq;
      case 'contains': return contains;
      case 'not_contains': return !contains;
      case 'greater_than':
      case 'less_than': {
        var x = src.type === 'checkbox_group' ? selectedIds(src, v).length
          : (src.type === 'text' || src.type === 'textarea') ? str(v).length
          : num(v);
        var y = num(target);
        if (x === null || y === null) return false;
        return op === 'greater_than' ? x > y : x < y;
      }
    }
    return false;
  }

  /**
   * Which fields are visible for the given values. A field whose controlling field is hidden
   * sees that field as empty, so chains of conditions collapse correctly. Iterates to a fixed
   * point so a condition may reference a field further down the form.
   */
  function computeVisibility(fields, values) {
    var visible = {};
    fields.forEach(function (f) { visible[f.id] = true; });
    for (var pass = 0; pass <= fields.length; pass++) {
      var changed = false;
      for (var i = 0; i < fields.length; i++) {
        var f = fields[i];
        if (!f.conditions) continue;
        var results = f.conditions.rules.map(function (rule) {
          var src = findField(fields, rule.field);
          if (!src || src.id === f.id) return false;
          return testRule(src, visible[src.id] ? values[src.id] : undefined, rule);
        });
        var ok = f.conditions.match === 'any' ? results.some(Boolean) : results.every(Boolean);
        var show = f.conditions.action === 'hide' ? !ok : ok;
        if (visible[f.id] !== show) { visible[f.id] = show; changed = true; }
      }
      if (!changed) break;
    }
    return visible;
  }

  function acceptMatches(accept, file) {
    if (!accept) return true;
    var name = norm(file.name);
    var type = norm(file.type);
    return accept.split(',').some(function (raw) {
      var a = norm(raw);
      if (!a) return false;
      if (a.charAt(0) === '.') return name.slice(-a.length) === a;
      if (a.slice(-2) === '/*') return type.indexOf(a.slice(0, -1)) === 0;
      return type === a;
    });
  }

  /** Returns an error message, or null when the value is valid. Hidden fields are never validated. */
  function validateField(field, v, messages) {
    var M = messages || DEFAULT_MESSAGES;
    var rules = field.validation || {};
    if (has(DISPLAY_TYPES, field.type)) return null;
    if (isEmpty(field, v)) {
      if (!field.required) return null;
      return has(CHOICE_TYPES, field.type) ? M.choose : M.required;
    }
    if (field.type === 'text' || field.type === 'textarea') {
      var s = str(v).trim();
      var minL = num(rules.minLength);
      var maxL = num(rules.maxLength);
      if (minL !== null && s.length < minL) return fmt(M.minLength, minL);
      if (maxL !== null && s.length > maxL) return fmt(M.maxLength, maxL);
      if (rules.pattern) {
        try {
          if (!new RegExp('^(?:' + rules.pattern + ')$').test(s)) return rules.patternMessage || M.pattern;
        } catch (e) { /* invalid pattern from config: ignore */ }
      }
    }
    if (field.type === 'number') {
      var n = num(v);
      if (n === null) return M.number;
      var min = num(rules.min);
      var max = num(rules.max);
      if (min !== null && n < min) return fmt(M.min, min);
      if (max !== null && n > max) return fmt(M.max, max);
    }
    if (field.type === 'date') {
      var ds = str(v).trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(ds)) return M.date;
      var dMin = resolveDate(rules.min);
      var dMax = resolveDate(rules.max);
      if (dMin && ds < dMin) return fmt(M.dateMin, displayDate(dMin));
      if (dMax && ds > dMax) return fmt(M.dateMax, displayDate(dMax));
    }
    if (field.type === 'checkbox_group') {
      var count = selectedIds(field, v).length;
      var minS = num(rules.minSelections);
      var maxS = num(rules.maxSelections);
      if (minS !== null && count < minS) return fmt(M.minSelections, minS);
      if (maxS !== null && count > maxS) return fmt(M.maxSelections, maxS);
    }
    if (field.type === 'file' && v) {
      var maxMb = num(rules.maxSizeMb);
      if (maxMb === null) maxMb = 20;
      if (v.size > maxMb * 1024 * 1024) return fmt(M.fileSize, maxMb);
      if (!acceptMatches(rules.accept, v)) return M.fileType;
    }
    return null;
  }

  /** The text stored as the line item property value. */
  function propertyValue(field, v) {
    if (has(CHOICE_TYPES, field.type)) {
      return selectedIds(field, v)
        .map(function (id) { var o = optionFor(field, id); return o ? o.label : ''; })
        .filter(Boolean)
        .join(', ');
    }
    if (field.type === 'checkbox') return v ? field.checkedValue : '';
    if (field.type === 'file') return v ? v.name : '';
    return str(v).trim();
  }

  function isPriced(field) {
    if (field.price > 0) return true;
    return field.options.some(function (o) { return o.price > 0; });
  }

  /**
   * Surcharge in shop-currency cents for the visible selections, plus the `_doof` payload the
   * pricing function reads. The pricing function recomputes this server-side from its own copy
   * of the prices, so this is only for display.
   */
  function priceSelections(fields, values, visible) {
    var total = 0;
    var items = [];
    var payload = {};
    fields.forEach(function (f) {
      if (!visible[f.id] || has(DISPLAY_TYPES, f.type) || !isPriced(f)) return;
      var v = values[f.id];
      if (isEmpty(f, v)) return;
      var amount = 0;
      if (has(CHOICE_TYPES, f.type)) {
        var ids = selectedIds(f, v);
        ids.forEach(function (id) { var o = optionFor(f, id); if (o) amount += o.price; });
        payload[f.id] = f.type === 'checkbox_group' ? ids.slice() : v;
      } else if (f.type === 'checkbox' || f.type === 'file') {
        amount = f.price;
        payload[f.id] = true;
      } else {
        var s = str(v).trim();
        if (f.priceMode === 'per_char') amount = f.price * s.replace(/\s/g, '').length;
        else if (f.priceMode === 'per_unit') amount = f.price * Math.max(0, num(s) || 0);
        else amount = f.price;
        payload[f.id] = s;
      }
      amount = Math.max(0, Math.round(amount));
      if (amount > 0) items.push({ fieldId: f.id, label: f.label || f.name, amount: amount });
      total += amount;
    });
    return { total: total, items: items, payload: payload };
  }

  function formatMoney(cents, opts) {
    var o = opts || {};
    var amount = (cents / 100) * (o.rate || 1);
    try {
      return new Intl.NumberFormat(o.locale || undefined, { style: 'currency', currency: o.currency || 'USD' }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  /**
   * Adds `cents` to the first amount in a theme-formatted price ("$139.95 USD", "1.234,56 €", "¥1,234"),
   * keeping its symbols, separators and decimals.
   */
  function addToAmount(text, cents) {
    var s = String(text);
    var m = /\d[\d.,'\s]*\d|\d/.exec(s); // \s includes the no-break spaces some currencies group with
    if (!m) return s;
    var run = m[0];
    var dec = '';
    var last = /[.,](\d+)$/.exec(run);
    if (last && last[1].length === 2) dec = run.charAt(run.length - 3); // "139.95", "1.234,56"
    var intPart = dec ? run.slice(0, -3) : run;
    var thou = (/[.,'\s]/.exec(intPart) || [''])[0];
    var units = parseInt(intPart.replace(/\D/g, ''), 10) || 0;
    var total = units * 100 + (dec ? parseInt(run.slice(-2), 10) : 0) + Math.round(cents);
    var whole = String(Math.floor(total / 100));
    if (thou) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thou);
    var out = dec ? whole + dec + pad2(total % 100) : String(Math.round(total / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thou);
    return s.slice(0, m.index) + out + s.slice(m.index + run.length);
  }

  var core = {
    addToAmount: addToAmount,
    CHOICE_TYPES: CHOICE_TYPES,
    DISPLAY_TYPES: DISPLAY_TYPES,
    normalizeConfig: normalizeConfig,
    variantValues: variantValues,
    resolveDate: resolveDate,
    findField: findField,
    isEmpty: isEmpty,
    initialValue: initialValue,
    testRule: testRule,
    computeVisibility: computeVisibility,
    validateField: validateField,
    propertyValue: propertyValue,
    priceSelections: priceSelections,
    activeCharges: activeCharges,
    chargePriceFor: chargePriceFor,
    formatMoney: formatMoney,
    acceptMatches: acceptMatches
  };

  global.DOOF = Object.assign(global.DOOF || {}, { version: VERSION, core: core });

  // ------------------------------------------------------------------ DOM element

  if (typeof HTMLElement === 'undefined' || !global.customElements || global.customElements.get('doof-form')) return;

  var instanceCount = 0;

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        var v = attrs[k];
        if (v === null || v === undefined || v === false) return;
        if (k === 'text') node.textContent = v;
        else if (k === 'html') node.innerHTML = v;
        else if (k === 'className') node.className = v;
        else if (k === 'dataset') Object.keys(v).forEach(function (d) { node.dataset[d] = v[d]; });
        else if (k === 'style') node.setAttribute('style', v);
        else node.setAttribute(k, v === true ? '' : v);
      });
    }
    (children || []).forEach(function (c) {
      if (c === null || c === undefined || c === false) return;
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
  }

  function moneyOpts() {
    var cur = global.Shopify && global.Shopify.currency || {};
    return {
      currency: cur.active || 'USD',
      rate: parseFloat(cur.rate) || 1,
      locale: document.documentElement.lang || undefined
    };
  }

  function isDesignMode() { return !!(global.Shopify && global.Shopify.designMode); }

  class DoofForm extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      // Config comes from <template data-doof-config="..."> (HTML-escaped attribute) or, as a fallback,
      // <script type="application/json" data-doof-config>.
      var holder = this.querySelector('[data-doof-config]');
      var text = !holder ? '{}' : holder.tagName === 'SCRIPT' ? holder.textContent : holder.getAttribute('data-doof-config');
      var raw;
      try {
        raw = JSON.parse(text || '{}');
      } catch (e) {
        console.error('[DOOF] Invalid config JSON', e);
        return;
      }
      this.cfg = normalizeConfig(raw);
      if (!this.cfg.fields.length) return;

      this.uid = 'doof' + (++instanceCount);
      this.fieldById = {};
      this.values = {};
      this.touched = {};
      this.nodes = {};
      var self = this;
      this.cfg.fields.forEach(function (f) {
        self.fieldById[f.id] = f;
        self.values[f.id] = initialValue(f);
      });

      this.removeStaleCopies();
      this.form = this.findForm();
      if (this.form && !this.form.id) this.form.id = this.uid + '-form';
      this.syncVariant();
      if (this.cfg.settings.matchTheme === true) {
        this.sampleTheme();
        // theme stylesheets that finish loading later: sample again once the page is complete
        if (document.readyState !== 'complete') global.addEventListener('load', function () { self.sampleTheme(); }, { once: true });
      }
      this.placeIntoForm();
      this.render();
      this.update();
      this.bindForm();
    }

    disconnectedCallback() {
      // Moving the element (placeIntoForm) also fires this; only clean up if it is really gone.
      var self = this;
      setTimeout(function () { if (!self.isConnected) self.unbindForm(); }, 0);
    }

    /**
     * Reads the selected variant from the product form into the product option pseudo-fields.
     * Returns true when it changed.
     */
    syncVariant() {
      var cfg = this.cfg;
      if (!cfg.optionFields.length) return false;
      var input = this.form && this.form.elements && this.form.elements.namedItem ? this.form.elements.namedItem('id') : null;
      var id = input ? str(input.value) : '';
      if (id === this._variantId) return false;
      this._variantId = id;
      Object.assign(this.values, variantValues(cfg, id));
      return true;
    }

    /** The main product area the form belongs to (where the theme's own option controls live). */
    productScope() {
      var f = this.form;
      return (f && (f.closest('product-info') || f.closest('[id^="MainProduct-"]') || f.closest('.shopify-section'))) || document;
    }

    /**
     * Copies the look of the theme's own product option controls (the variant picker, else the quantity box)
     * into CSS variables, so DOOF's labels, boxes and dropdowns match them exactly: font, size, weight,
     * colour, height, border, corner radius, spacing, and the dropdown arrow icon itself.
     */
    sampleTheme() {
      var scope = this.productScope();
      var q = function (sel) { try { return scope.querySelector(sel); } catch (e) { return null; } };
      var label = q('variant-selects .form__label, variant-radios legend, .product-form__input .form__label, .product-form__input legend, label[for^="Quantity"]');
      var select = q('variant-selects select, variant-radios select, .product-form__input select');
      var box = select ? (select.closest('.select') || select) : q('.product-form__input .quantity, quantity-input');
      var group = (label || box) && (label || box).closest('.product-form__input');
      var style = this.style;
      var css = function (el, pseudo) {
        try { return global.getComputedStyle(el, pseudo || null); } catch (e) { return null; }
      };
      var set = function (name, value) {
        if (value && value !== 'normal' && value !== 'none' && value !== 'auto') style.setProperty(name, value);
      };
      if (label) {
        var ls = css(label);
        if (ls) {
          set('--doof-label-font-family', ls.fontFamily);
          set('--doof-label-font-size', ls.fontSize);
          set('--doof-label-font-weight', ls.fontWeight);
          set('--doof-label-letter-spacing', ls.letterSpacing);
          set('--doof-label-transform', ls.textTransform);
          set('--doof-label-color', ls.color);
          set('--doof-label-line-height', ls.lineHeight);
          set('--doof-label-gap', ls.marginBottom);
        }
      }
      var control = select || (box && box.querySelector('input'));
      if (control) {
        var cs = css(control);
        if (cs) {
          set('--doof-control-font-family', cs.fontFamily);
          set('--doof-control-font-size', cs.fontSize);
          set('--doof-control-color', cs.color);
          set('--doof-control-letter-spacing', cs.letterSpacing);
          if (select && parseFloat(cs.paddingLeft) > 0) set('--doof-control-padding', cs.paddingLeft);
        }
      }
      if (box) {
        // The border can be on the control itself (themes that restyle Dawn's dropdown, e.g. Sunstone's 4px
        // rounded box), on its wrapper, or drawn by the wrapper's ::after box-shadow (plain Dawn). Prefer the
        // control's own, since that is what's visible when a theme overrides the default.
        var hasBorder = function (s) { return s && parseFloat(s.borderTopWidth) > 0 && s.borderTopStyle !== 'none'; };
        var selfStyle = control ? css(control) : null;
        var wrapStyle = box !== control ? css(box) : null;
        var after = css(box, '::after');
        var shadow = after && after.boxShadow && after.boxShadow !== 'none' ? after.boxShadow : '';
        var src = hasBorder(selfStyle) ? control : hasBorder(wrapStyle) ? box : null;
        var srcStyle = src === control ? selfStyle : wrapStyle;
        if (src) {
          set('--doof-control-border', 'inset 0 0 0 ' + srcStyle.borderTopWidth + ' ' + srcStyle.borderTopColor);
        } else if (shadow && shadow.split(/,(?![^(]*\))/).length === 1) {
          set('--doof-control-border', 'inset ' + shadow);
        }
        // the roundest corner among them is the visible one
        var radius = [selfStyle, wrapStyle, after].reduce(function (r, s) {
          var v = s ? parseFloat(s.borderTopLeftRadius) : NaN;
          return !isNaN(v) && v > r ? v : r;
        }, 0);
        set('--doof-control-radius', radius + 'px');
        var hEl = src === control || !src && control && control === select ? control : box;
        var h = hEl.getBoundingClientRect ? hEl.getBoundingClientRect().height : 0;
        if (h > 0) set('--doof-control-height', Math.round(h) + 'px');
        var bg = selfStyle && selfStyle.backgroundColor;
        if (bg && bg !== 'transparent' && !/rgba\([^)]*,\s*0\)$/.test(bg)) set('--doof-control-bg', bg);
      }
      if (group) {
        var gs = css(group);
        if (gs) {
          set('--doof-field-gap', gs.marginBottom);
          set('--doof-max-width', gs.maxWidth);
        }
      }
      var caret = select && select.parentElement && select.parentElement.querySelector('svg');
      if (caret) this.caretHtml = caret.outerHTML;
    }

    /** The theme editor re-renders sections; drop copies we previously moved into the product form. */
    removeStaleCopies() {
      var sid = this.dataset.sectionId;
      if (!sid) return;
      var self = this;
      document.querySelectorAll('doof-form[data-section-id="' + sid + '"]').forEach(function (other) {
        if (other !== self) other.remove();
      });
    }

    findForm() {
      var sel = this.cfg.settings.formSelector;
      if (sel) {
        try {
          var hit = document.querySelector(sel);
          if (hit) return hit.tagName === 'FORM' ? hit : hit.querySelector('form');
        } catch (e) { console.warn('[DOOF] Invalid product form selector', sel); }
      }
      var inside = this.closest('form[action*="/cart/add"]');
      if (inside) return inside;

      var usable = function (f) {
        return !/installment/i.test(f.id || '') &&
          !f.closest('[id*="quick"], .quick-add, quick-add-modal, product-recommendations, [data-doof-ignore]') &&
          !!f.querySelector('[type="submit"], button[name="add"], button:not([type])');
      };
      // Most specific first. Dawn and its forks wrap the main product in <product-info id="MainProduct-...">.
      var scopes = [
        this.closest('.shopify-section'),
        document.querySelector('product-info[id^="MainProduct-"]'),
        document.querySelector('[id^="MainProduct-"]'),
        document.querySelector('[id*="main-product"], [id*="main_product"], .product-section'),
        document.querySelector('main'),
        document
      ];
      for (var i = 0; i < scopes.length; i++) {
        if (!scopes[i]) continue;
        var forms = Array.prototype.slice.call(scopes[i].querySelectorAll('form[action*="/cart/add"]')).filter(usable);
        if (forms.length) return forms[0];
      }
      return null;
    }

    /** Moves the fields inside the product form, just above the add-to-cart buttons. */
    placeIntoForm() {
      var form = this.form;
      var placement = this.cfg.settings.placement;
      if (!form || placement === 'section' || this._placed) return;
      this._placed = true;
      var anchor = null;
      var before = this.cfg.settings.insertBefore;
      if (before) {
        try { anchor = form.querySelector(before) || document.querySelector(before); } catch (e) { anchor = null; }
        if (anchor) { anchor.parentNode.insertBefore(this, anchor); return; }
      }
      // Default: straight after the theme's variant picker, so all the choices sit together. The fields
      // may then be outside the <form>; their inputs carry form="<form id>" (like the theme's own quantity box).
      if (placement !== 'auto') {
        var scope = this.productScope();
        var pickers = scope.querySelectorAll('variant-selects, variant-radios');
        var picker = pickers.length ? pickers[pickers.length - 1] : null;
        if (picker && !picker.closest('quick-add-modal, .quick-add, product-recommendations')) {
          picker.parentNode.insertBefore(this, picker.nextSibling);
          return;
        }
        var qty = scope.querySelector('[id^="Quantity-Form-"], .product-form__quantity');
        if (qty && !form.contains(qty)) { qty.parentNode.insertBefore(this, qty); return; }
      }
      if (form.contains(this)) return;
      var btn = form.querySelector('button[name="add"], [type="submit"], button:not([type])');
      if (!btn) { form.appendChild(this); return; }
      var node = btn;
      while (node.parentElement && node.parentElement !== form) node = node.parentElement;
      form.insertBefore(this, node.parentElement === form ? node : null);
    }

    render() {
      var self = this;
      var cfg = this.cfg;
      var M = cfg.messages;
      var root = el('div', { className: 'doof' });

      if (cfg.settings.heading) root.appendChild(el('p', { className: 'doof__heading', text: cfg.settings.heading }));

      if (isDesignMode()) {
        cfg.charges.forEach(function (c) {
          if (c.missing) root.appendChild(el('p', { className: 'doof-notice', text: fmt(M.chargeMissingEditor, c.title) }));
        });
        if (!this.form) root.appendChild(el('p', { className: 'doof-notice', text: M.formMissing }));
        else if (this.form.querySelector('.shopify-payment-button, [data-shopify="payment-button"]')) {
          root.appendChild(el('p', { className: 'doof-notice', text: M.dynamicCheckout }));
        }
      }

      var list = el('div', { className: 'doof__fields' });
      cfg.fields.forEach(function (f) { list.appendChild(self.renderField(f)); });
      root.appendChild(list);

      if (cfg.pricing || cfg.charges.length) {
        this.summary = el('div', { className: 'doof-summary', 'aria-live': 'polite', hidden: true });
        root.appendChild(this.summary);
      }
      this.buyError = el('p', { className: 'doof-error doof-buy-error', role: 'alert', hidden: true });
      root.appendChild(this.buyError);

      this.hiddenBox = el('div', { className: 'doof__inputs', hidden: true });
      cfg.fields.forEach(function (f) {
        if (has(DISPLAY_TYPES, f.type) || f.type === 'file') return;
        var input = el('input', { type: 'hidden', name: 'properties[' + f.name + ']' });
        self.nodes[f.id].hidden = input;
        self.hiddenBox.appendChild(input);
      });
      if (cfg.pricing) {
        this.pricingInput = el('input', { type: 'hidden', name: 'properties[_doof]' });
        this.hiddenBox.appendChild(this.pricingInput);
      }
      root.appendChild(this.hiddenBox);

      if (this.form) {
        var formId = this.form.id;
        root.querySelectorAll('input[name^="properties["]').forEach(function (i) { i.setAttribute('form', formId); });
      }

      root.addEventListener('change', function (e) { self.onInput(e); });
      root.addEventListener('input', function (e) { self.onInput(e); });

      this.appendChild(root);
      this.root = root;
    }

    renderField(f) {
      var self = this;
      var cfg = this.cfg;
      var M = cfg.messages;
      var id = this.uid + '-' + f.id;
      var wrap = el('div', {
        className: 'doof-field doof-field--' + f.type.replace(/_/g, '-') + ' doof-field--' + f.width,
        dataset: { fieldId: f.id }
      });
      var nodes = { wrap: wrap };
      this.nodes[f.id] = nodes;

      if (f.type === 'heading') { wrap.appendChild(el('p', { className: 'doof-heading', text: f.label })); return wrap; }
      if (f.type === 'paragraph') { wrap.appendChild(el('div', { className: 'doof-paragraph', html: f.content || f.help })); return wrap; }
      if (f.type === 'divider') { wrap.appendChild(el('hr', { className: 'doof-divider' })); return wrap; }

      var helpId = id + '-help';
      var errId = id + '-error';
      var describedBy = (f.help ? helpId + ' ' : '') + errId;
      var mark = f.required && cfg.settings.requiredMark !== false
        ? el('span', { className: 'doof-required', 'aria-hidden': 'true', text: ' *' }) : null;

      // "(+$5.00)" next to a choice: its own price (pricing app) plus any extra charge it switches on
      // (charges are already in the shopper's currency, so no conversion rate for them)
      var priceSuffix = function (cents, option) {
        var opts = moneyOpts();
        var total = (cfg.pricing ? cents * opts.rate : 0) + chargePriceFor(f, cfg.charges, option);
        return total > 0 ? ' (+' + formatMoney(Math.round(total), Object.assign({}, opts, { rate: 1 })) + ')' : '';
      };

      var isGroup = has(GROUP_TYPES, f.type);
      var labelNode;
      if (isGroup) {
        nodes.selected = el('span', { className: 'doof-selected' });
        labelNode = el('legend', { className: 'doof-label' }, [f.label, mark, nodes.selected]);
      } else if (f.type !== 'checkbox' || f.checkboxText) {
        // a checkbox with its own text gets the label as a title above it
        labelNode = el('label', { className: 'doof-label', for: id }, [f.label, mark]);
      }

      var control;
      var shown = null; // what goes on the page when it isn't the control itself
      // Visible controls point at a form id that doesn't exist, so they are never submitted and never
      // trigger native constraint validation (which breaks on hidden conditional fields). Only the hidden
      // `properties[...]` inputs and the file input belong to the product form. DOOF validates itself.
      var detached = this.uid + '-detached';
      var ariaReq = f.required ? 'true' : null;
      var common = { id: id, form: detached, dataset: { fieldId: f.id }, 'aria-describedby': describedBy, 'aria-required': ariaReq };
      var v = this.values[f.id];

      if (f.type === 'dropdown') {
        control = el('select', Object.assign({ className: 'doof-select' }, common));
        control.appendChild(el('option', { value: '', text: f.placeholder || M.chooseOption }));
        f.options.forEach(function (o) {
          control.appendChild(el('option', { value: o.id, text: o.label + priceSuffix(o.price, o), selected: o.id === v }));
        });
        // the theme's own dropdown arrow when there is one, else a drawn chevron
        var caret = el('span', { className: 'doof-select__caret' + (this.caretHtml ? '' : ' doof-select__caret--drawn'), 'aria-hidden': 'true' });
        if (this.caretHtml) caret.innerHTML = this.caretHtml;
        shown = el('div', { className: 'doof-select-wrap' }, [control, caret]);
      } else if (isGroup) {
        var multi = f.type === 'checkbox_group';
        control = el('div', { className: 'doof-choices doof-choices--' + f.type.replace(/_/g, '-'), role: multi ? 'group' : 'radiogroup' });
        f.options.forEach(function (o, i) {
          var inputId = id + '-' + i;
          var checked = multi ? (v || []).indexOf(o.id) !== -1 : v === o.id;
          var input = el('input', {
            type: multi ? 'checkbox' : 'radio',
            className: 'doof-choice__input',
            id: inputId,
            name: self.uid + '-' + f.id,
            form: detached,
            value: o.id,
            checked: checked,
            dataset: { fieldId: f.id },
            'aria-describedby': describedBy
          });
          var ui;
          if (f.type === 'color_swatch') {
            ui = el('span', { className: 'doof-choice__swatch', style: o.color ? '--doof-swatch:' + o.color.replace(/[;"<>]/g, '') : null }, [
              o.image ? el('img', { src: o.image, alt: '', loading: 'lazy' }) : null
            ]);
          } else if (f.type === 'image_swatch') {
            ui = el('span', { className: 'doof-choice__image' }, [
              o.image ? el('img', { src: o.image, alt: '', loading: 'lazy' })
                : el('span', { className: 'doof-choice__swatch', style: o.color ? '--doof-swatch:' + o.color.replace(/[;"<>]/g, '') : null })
            ]);
          }
          var text = el('span', { className: 'doof-choice__label' }, [o.label, priceSuffix(o.price, o)]);
          control.appendChild(el('label', { className: 'doof-choice', for: inputId, title: o.label + priceSuffix(o.price, o) }, [input, ui, text]));
        });
      } else if (f.type === 'checkbox') {
        var cb = el('input', Object.assign({ type: 'checkbox', className: 'doof-check__input', checked: !!v }, common));
        var boxText = f.checkboxText ? [f.checkboxText, priceSuffix(f.price)] : [f.label, priceSuffix(f.price), mark];
        control = el('label', { className: 'doof-check', for: id }, [cb, el('span', { className: 'doof-check__label' }, boxText)]);
      } else if (f.type === 'textarea') {
        control = el('textarea', Object.assign({
          className: 'doof-input doof-textarea', rows: 3, placeholder: f.placeholder || null,
          maxlength: num(f.validation.maxLength)
        }, common));
        control.value = str(v);
      } else if (f.type === 'file') {
        control = el('input', Object.assign({}, common, {
          type: 'file', className: 'doof-file__input', name: 'properties[' + f.name + ']',
          form: null, accept: f.validation.accept || null
        }));
        nodes.file = control;
        nodes.fileName = el('span', { className: 'doof-file__name', 'aria-live': 'polite', text: M.noFile });
        nodes.filePreview = el('img', { className: 'doof-file__preview', alt: '', hidden: true });
        nodes.fileRemove = el('button', { type: 'button', className: 'doof-file__remove', hidden: true, text: M.removeFile });
        nodes.fileRemove.addEventListener('click', function () {
          control.value = '';
          self.values[f.id] = null;
          self.touched[f.id] = true;
          self.showFile(f);
          self.update();
          control.focus();
        });
        shown = el('div', { className: 'doof-file' }, [
          control,
          el('label', { className: 'doof-file__button', for: id }, [
            el('span', {
              className: 'doof-file__icon', 'aria-hidden': 'true',
              html: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13V3M6 7l4-4 4 4M3 13v3a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3"/></svg>'
            }),
            // images only (e.g. image/* or .png,.jpg): "Upload your photo", as the customizer says
            (f.validation.accept && str(f.validation.accept).split(',').every(function (a) {
              return /^\s*(image\/|\.(png|jpe?g|gif|webp|heic|heif)\s*$)/i.test(a);
            })) ? M.choosePhoto : M.chooseFile
          ]),
          nodes.filePreview,
          nodes.fileName,
          nodes.fileRemove
        ]);
      } else {
        var type = { text: 'text', number: 'number', date: 'date', color_picker: 'color' }[f.type] || 'text';
        control = el('input', Object.assign({
          type: type,
          className: 'doof-input',
          placeholder: f.placeholder || null,
          maxlength: f.type === 'text' ? num(f.validation.maxLength) : null,
          min: f.type === 'number' ? str(f.validation.min) || null : f.type === 'date' ? resolveDate(f.validation.min) || null : null,
          max: f.type === 'number' ? str(f.validation.max) || null : f.type === 'date' ? resolveDate(f.validation.max) || null : null,
          step: f.type === 'number' ? str(f.validation.step) || 'any' : null,
          inputmode: f.type === 'number' ? 'decimal' : null
        }, common));
        control.value = str(v) || (f.type === 'color_picker' ? '#000000' : '');
        if (f.type === 'color_picker' && !v) self.values[f.id] = control.value;
      }

      nodes.control = control;
      var body = [labelNode, shown || control];

      if ((f.type === 'text' || f.type === 'textarea') && num(f.validation.maxLength)) {
        nodes.counter = el('span', { className: 'doof-counter', 'aria-hidden': 'true' });
        body.push(nodes.counter);
      }
      if (f.type !== 'file' && f.price > 0 && cfg.pricing && f.type !== 'checkbox') {
        var unit = f.priceMode === 'per_char' ? ' / character' : f.priceMode === 'per_unit' ? ' / unit' : '';
        body.push(el('p', { className: 'doof-price-note', text: '+' + formatMoney(f.price, moneyOpts()) + unit }));
      }
      if (!has(CHOICE_TYPES, f.type) && f.type !== 'checkbox') {
        var whenFilled = chargePriceFor(f, cfg.charges);
        if (whenFilled > 0) {
          body.push(el('p', { className: 'doof-price-note', text: '+' + formatMoney(whenFilled, Object.assign({}, moneyOpts(), { rate: 1 })) }));
        }
      }
      if (f.help) body.push(el('p', { className: 'doof-help', id: helpId, text: f.help }));
      nodes.error = el('p', { className: 'doof-error', id: errId, hidden: true });
      body.push(nodes.error);

      if (isDesignMode() && f.conditions) {
        f.conditions.rules.forEach(function (r) {
          if (!findField(cfg.ruleFields, r.field)) body.push(el('p', { className: 'doof-notice', text: fmt(M.ruleMissing, r.field) }));
        });
      }

      var container = isGroup ? el('fieldset', { className: 'doof-fieldset' }, body) : el('div', { className: 'doof-fieldset' }, body);
      wrap.appendChild(container);
      return wrap;
    }

    onInput(e) {
      var target = e.target;
      var fid = target && target.dataset && target.dataset.fieldId;
      var f = fid && this.fieldById[fid];
      if (!f) return;
      var v;
      if (f.type === 'checkbox_group') {
        v = Array.prototype.slice.call(this.nodes[fid].control.querySelectorAll('input:checked')).map(function (i) { return i.value; });
      } else if (f.type === 'checkbox') {
        v = target.checked;
      } else if (f.type === 'file') {
        v = target.files && target.files[0] || null;
      } else {
        v = target.value;
      }
      this.values[fid] = v;
      if (f.type === 'file') this.showFile(f);
      if (e.type === 'change' || this.touched[fid]) this.touched[fid] = true;
      this.update();
    }

    /** File name, thumbnail (images) and Remove for an upload field. */
    showFile(f) {
      var n = this.nodes[f.id];
      var file = this.values[f.id];
      n.fileName.textContent = file ? file.name : this.cfg.messages.noFile;
      n.fileRemove.hidden = !file;
      if (n.previewUrl && global.URL && global.URL.revokeObjectURL) global.URL.revokeObjectURL(n.previewUrl);
      n.previewUrl = null;
      if (file && /^image\//.test(file.type || '') && global.URL && typeof global.URL.createObjectURL === 'function') {
        n.previewUrl = global.URL.createObjectURL(file);
        n.filePreview.src = n.previewUrl;
        n.filePreview.hidden = false;
      } else {
        n.filePreview.removeAttribute('src');
        n.filePreview.hidden = true;
      }
    }

    update() {
      var self = this;
      var cfg = this.cfg;
      var visible = computeVisibility(cfg.ruleFields, this.values);
      this.visible = visible;

      cfg.fields.forEach(function (f) {
        var n = self.nodes[f.id];
        var show = visible[f.id];
        n.wrap.hidden = !show;
        if (has(DISPLAY_TYPES, f.type)) return;
        var v = self.values[f.id];

        if (n.hidden) {
          n.hidden.value = propertyValue(f, v);
          n.hidden.disabled = !show || isEmpty(f, v);
        }
        if (n.file) n.file.disabled = !show;
        if (n.selected) {
          var label = propertyValue(f, v);
          n.selected.textContent = label && (f.type === 'color_swatch' || f.type === 'image_swatch') ? ': ' + label : '';
        }
        if (n.counter) n.counter.textContent = str(v).length + '/' + num(f.validation.maxLength);
        if (self.touched[f.id]) self.showError(f, show ? validateField(f, v, cfg.messages) : null);
      });

      var priced = cfg.pricing ? priceSelections(cfg.fields, this.values, visible) : { total: 0, items: [], payload: {} };
      if (cfg.pricing) {
        var hasPayload = Object.keys(priced.payload).length > 0;
        this.pricingInput.value = hasPayload ? JSON.stringify({ v: 1, f: priced.payload }) : '';
        this.pricingInput.disabled = !hasPayload;
      }
      this.charges = activeCharges(cfg.ruleFields, this.values, visible, cfg.charges);
      if (this._refused && !this.missingCharge()) { this._refused = false; this.buyError.hidden = true; }

      // One summary in the shopper's currency: pricing-app amounts are shop-currency cents (converted),
      // extra charges are already presentment cents.
      var rate = moneyOpts().rate;
      var lines = priced.items.map(function (i) { return { label: i.label, amount: Math.round(i.amount * rate) }; })
        .concat(this.charges.filter(function (c) { return !c.missing; }).map(function (c) { return { label: c.title, amount: c.price }; }));
      if (this.summary) this.renderSummary(lines);

      this.updatePrice();

      this.dispatchEvent(new CustomEvent('doof:change', {
        bubbles: true,
        detail: { values: this.values, visible: visible, surcharge: priced.total, items: priced.items, charges: this.charges }
      }));
    }

    renderSummary(lines) {
      var s = this.summary;
      var total = lines.reduce(function (a, l) { return a + l.amount; }, 0);
      s.textContent = '';
      s.hidden = total <= 0;
      if (total <= 0) return;
      var opts = Object.assign({}, moneyOpts(), { rate: 1 });
      var M = this.cfg.messages;
      s.appendChild(el('p', { className: 'doof-summary__total' }, [
        (this.cfg.charges.length ? M.extraCharges : M.options) + ': ',
        el('strong', { text: '+' + formatMoney(total, opts) })
      ]));
      var ul = el('ul', { className: 'doof-summary__items' });
      lines.forEach(function (l) { ul.appendChild(el('li', { text: l.label + ' +' + formatMoney(l.amount, opts) })); });
      s.appendChild(ul);
    }

    /**
     * Adds the item plus one line per active extra charge, the way the stencil customizer does:
     * the item first (the form's own data, so files and selling plans go through untouched), then each
     * add-on labelled "For: <product> (<variant>)". If an add-on fails, the item is taken back out so
     * nobody gets the options without the charge. Then the store's cart drawer opens, or the cart page.
     */
    addWithCharges() {
      var self = this;
      var cfg = this.cfg;
      var form = this.form;
      var charges = this.charges.slice();
      var fd = new FormData(form);
      var qty = Math.max(1, parseInt(fd.get('quantity'), 10) || 1);
      var root = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
      var btn = form.querySelector('button[name="add"], [type="submit"]');
      var btnText = btn ? btn.innerHTML : '';

      // fields a charge carries on its own line (e.g. the engraving words on the Engraving text line).
      // Per charge, not per variant: front and back engraving can use the same add-on product.
      var moved = charges.map(function (c) {
        var props = [];
        c.moveFields.forEach(function (ref) {
          var f = findField(cfg.fields, ref);
          if (!f || !self.visible[f.id]) return;
          var val = propertyValue(f, self.values[f.id]);
          if (!val) return;
          props.push([f.name, val]);
          fd.delete('properties[' + f.name + ']');
        });
        return props;
      });

      var post = function (path, body, json) {
        return global.fetch(root + path, {
          method: 'POST',
          headers: json ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
          body: json ? JSON.stringify(body) : body
        }).then(function (r) {
          return r.json().catch(function () { return {}; }).then(function (j) {
            if (!r.ok) throw new Error(j.description || j.message || ('error ' + r.status));
            return j;
          });
        });
      };

      this.buyError.hidden = true;
      if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
      var item;
      return post('cart/add.js', fd)
        .then(function (j) {
          item = j;
          var forLabel = (j.product_title || cfg.product.title || '') +
            (j.variant_title && j.variant_title !== 'Default Title' ? ' (' + j.variant_title + ')' : '');
          return charges.reduce(function (chain, c, ci) {
            return chain.then(function () {
              var fa = new FormData();
              fa.append('id', c.variantId);
              fa.append('quantity', String(c.qty === 'one' ? 1 : qty));
              if (forLabel) fa.append('properties[For]', forLabel);
              moved[ci].forEach(function (p) { fa.append('properties[' + p[0] + ']', p[1]); });
              return post('cart/add.js', fa);
            });
          }, Promise.resolve()).catch(function (err) {
            // take the item back out (only the quantity just added) and report
            var left = Math.max(0, (item.quantity || qty) - qty);
            return post('cart/change.js', { id: item.key, quantity: left }, true).catch(function () {}).then(function () { throw err; });
          });
        })
        .then(function () {
          if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); btn.innerHTML = btnText; }
          var evt = new CustomEvent('doof:added', { bubbles: true, cancelable: true, detail: { item: item, charges: charges } });
          if (!self.dispatchEvent(evt)) return; // a theme integration handled it
          var drawer = global.SunstoneCart;
          if (drawer && typeof drawer.open === 'function') {
            Promise.resolve(typeof drawer.refresh === 'function' ? drawer.refresh() : null).then(drawer.open, drawer.open);
            return;
          }
          global.location.href = root + 'cart';
        })
        .catch(function (err) {
          if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); btn.innerHTML = btnText; }
          self.buyError.textContent = fmt(cfg.messages.addError, (err && err.message) || err);
          self.buyError.hidden = false;
        });
    }

    showError(f, msg) {
      var n = this.nodes[f.id];
      n.error.textContent = msg || '';
      n.error.hidden = !msg;
      n.wrap.classList.toggle('doof-field--invalid', !!msg);
      var inputs = n.control.matches('input, select, textarea') ? [n.control] : n.control.querySelectorAll('input');
      Array.prototype.forEach.call(inputs, function (i) {
        if (msg) i.setAttribute('aria-invalid', 'true'); else i.removeAttribute('aria-invalid');
      });
    }

    /** Validates every visible field; focuses the first invalid one. Returns true when the form may submit. */
    validateAll() {
      var self = this;
      var first = null;
      if (this.syncVariant()) this.update();
      var visible = computeVisibility(this.cfg.ruleFields, this.values);
      this.cfg.fields.forEach(function (f) {
        if (has(DISPLAY_TYPES, f.type)) return;
        self.touched[f.id] = true;
        var msg = visible[f.id] ? validateField(f, self.values[f.id], self.cfg.messages) : null;
        self.showError(f, msg);
        if (msg && !first) first = f;
      });
      if (first) {
        var n = this.nodes[first.id];
        var focusable = n.control.matches('input, select, textarea') ? n.control : n.control.querySelector('input');
        if (n.wrap.scrollIntoView) n.wrap.scrollIntoView({ block: 'center', behavior: 'smooth' });
        if (focusable) focusable.focus({ preventScroll: true });
        return false;
      }
      return true;
    }

    bindForm() {
      var self = this;
      var form = this.form;
      if (!form) return;

      if (this.cfg.fields.some(function (f) { return f.type === 'file'; })) form.setAttribute('enctype', 'multipart/form-data');

      this._onSubmit = function (e) {
        if (e.target !== form) return;
        var ok = self.validateAll();
        var missing = ok && self.missingCharge();
        if (ok && !missing && !(self.charges && self.charges.length)) return; // no extra charge: the theme adds to cart as usual
        e.preventDefault();
        e.stopImmediatePropagation();
        if (missing) self.refuseMissing(missing);
        else if (ok) self.addWithCharges();
      };
      this._onClick = function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        var btn = t.closest('button, input[type="submit"], .shopify-payment-button');
        if (!btn || self.contains(btn)) return;
        var submits = btn.classList.contains('shopify-payment-button')
          ? form.contains(btn) || (!!btn.closest('.shopify-section') && btn.closest('.shopify-section') === form.closest('.shopify-section'))
          : btn.form === form && btn.type === 'submit';
        if (!submits) return;
        var valid = self.validateAll();
        var missing = valid && self.missingCharge();
        var buyNowWithCharges = valid && btn.classList.contains('shopify-payment-button') && self.charges && self.charges.length;
        if (valid && !buyNowWithCharges && !missing) return;
        if (missing) {
          self.refuseMissing(missing);
        } else if (buyNowWithCharges) {
          self.buyError.textContent = self.cfg.messages.buyNowCharges;
          self.buyError.hidden = false;
        }
        e.preventDefault();
        e.stopImmediatePropagation();
      };
      // window + capture runs before any document-level capture listener, whatever the script order.
      // Needed for themes whose cart drawer intercepts submit on document and stops propagation
      // (e.g. Sunstone's custom drawer, which loads before doof.js).
      global.addEventListener('submit', this._onSubmit, true);
      global.addEventListener('click', this._onClick, true);

      // Variant changes: Dawn-based themes set input[name="id"] and fire a bubbling change on it.
      if (this.cfg.optionFields.length) {
        this._onChange = function () { if (self.syncVariant()) self.update(); };
        global.addEventListener('change', this._onChange, true);
      }
    }

    /**
     * Shows the product price including the active extra charges (e.g. $139.95 -> $179.95 with engraving), in the
     * theme's own price block. The theme rewrites that block on variant changes; an observer re-adds the charges.
     */
    updatePrice() {
      var self = this;
      if (this.cfg.settings.updatePrice === false || !this.form) return;
      if (!this._priceBox || !this._priceBox.isConnected) {
        var scope = this.productScope();
        this._priceBox = scope.querySelector('[id^="price-"]') || scope.querySelector('.price');
        if (this._priceObs) this._priceObs.disconnect();
        this._priceObs = null;
        if (!this._priceBox) return;
        this._priceBase = new WeakMap();
        this._priceWritten = new WeakMap();
        if (typeof global.MutationObserver === 'function') {
          this._priceObs = new global.MutationObserver(function () { self.updatePrice(); });
          this._priceObs.observe(this._priceBox, { childList: true, subtree: true, characterData: true });
        }
      }
      var extra = (this.charges || []).reduce(function (sum, c) { return c.missing ? sum : sum + c.price; }, 0);
      var base = this._priceBase;
      var written = this._priceWritten;
      Array.prototype.forEach.call(this._priceBox.querySelectorAll('.price-item'), function (el) {
        // text the theme put there (first sight, or rewritten since we last wrote) is the base price
        if (!base.has(el) || el.textContent !== written.get(el)) base.set(el, el.textContent);
        var text = extra > 0 ? addToAmount(base.get(el), extra) : base.get(el);
        if (el.textContent !== text) el.textContent = text;
        written.set(el, text);
      });
      if (this._priceObs) this._priceObs.takeRecords(); // our own writes
    }

    /** An active charge whose add-on product isn't available, if any. */
    missingCharge() {
      return (this.charges || []).filter(function (c) { return c.missing; })[0] || null;
    }

    refuseMissing(c) {
      this._refused = true;
      this.buyError.textContent = fmt(this.cfg.messages.chargeMissing, c.title);
      this.buyError.hidden = false;
      if (this.buyError.scrollIntoView) this.buyError.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    unbindForm() {
      if (this._onSubmit) global.removeEventListener('submit', this._onSubmit, true);
      if (this._onClick) global.removeEventListener('click', this._onClick, true);
      if (this._onChange) global.removeEventListener('change', this._onChange, true);
      if (this._priceObs) {
        this._priceObs.disconnect();
        this._priceObs = null;
        // put the theme's price back
        var base = this._priceBase;
        if (this._priceBox && base) {
          Array.prototype.forEach.call(this._priceBox.querySelectorAll('.price-item'), function (el) {
            if (base.has(el)) el.textContent = base.get(el);
          });
        }
        this._priceBox = null;
      }
      this._onSubmit = this._onClick = this._onChange = null;
    }
  }

  global.customElements.define('doof-form', DoofForm);
})(typeof window !== 'undefined' ? window : globalThis);
